// Module Profil - Interface Candidat
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useSession } from '@/contexts/SessionContext';
import { CandidateServices } from '@/services/CandidateServices';
import { Candidate } from '@/models';
import { useNotification } from '@/contexts/NotificationContext';
import {
  FiUser, FiMail, FiPhone, FiMapPin, FiEdit2,
  FiSave, FiX, FiCalendar, FiBriefcase, FiUsers, FiHome
} from 'react-icons/fi';

// Types
interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  city: string;
  postalCode: string;
  poste_occupe: string;
  monthlyIncome: string;
  familySize: number;
  housingType: string;
}

// Styled Components
const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const ProfileContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${theme.spacing.xl};

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
`;

const ProfileSummary = styled(Card)`
  text-align: center;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.sizes['4xl']};
  font-weight: ${theme.typography.weights.bold};
  margin: 0 auto ${theme.spacing.lg};
`;

const UserName = styled.h2`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const UserEmail = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0 0 ${theme.spacing.lg} 0;
`;

const ProfileStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.surface};
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-top: ${theme.spacing.xs};
`;

const ProfileDetails = styled(Card)``;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }
`;

const CancelButton = styled(EditButton)`
  background: ${theme.colors.surface};
  color: ${theme.colors.text};

  &:hover {
    background: ${theme.colors.text}20;
  }
`;

const Form = styled.form``;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ fullWidth?: boolean }>`
  grid-column: ${props => props.fullWidth ? 'span 2' : 'span 1'};

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-family: ${theme.typography.fontFamily};
  transition: border-color ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &:disabled {
    background: ${theme.colors.surface};
    cursor: not-allowed;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: start;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} 0;
  border-bottom: 1px solid ${theme.colors.surface};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary}15;
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-bottom: ${theme.spacing.xs};
`;

const InfoValue = styled.div`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  font-weight: ${theme.typography.weights.medium};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
`;

export const CandidateProfile: React.FC = () => {
  const { loggedUser: user } = useSession();
  const notification = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [candidateData, setCandidateData] = useState<Candidate | null>(null);
  const [applicationsCount, setApplicationsCount] = useState(0);
  const [documentsCount, setDocumentsCount] = useState(0);

  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    postalCode: '',
    poste_occupe: '',
    monthlyIncome: '',
    familySize: 0,
    housingType: ''
  });

  // Charger les données du candidat au montage
  useEffect(() => {
    loadCandidateData();
  }, []);

  const loadCandidateData = async () => {
    try {
      setLoading(true);
      const response = await CandidateServices.getUserFolder();
      const result = response as {
        success: boolean;
        data?: {
          candidate: Candidate;
          files: any[];
        };
      };

      if (result.success && result.data) {
        const candidate = result.data.candidate;
        setCandidateData(candidate);
        setDocumentsCount(result.data.files?.length || 0);

        // Mettre à jour profileData avec les données du candidat
        setProfileData({
          firstName: user?.first_name || candidate.prenoms || '',
          lastName: user?.last_name || candidate.nom || '',
          email: user?.email || candidate.email || '',
          phone: candidate.telephone_1 || '',
          dateOfBirth: candidate.date_naissance || '',
          address: candidate.quartier_actuel || '',
          city: candidate.commune || '',
          postalCode: '',
          poste_occupe: candidate.activite_principale || '',
          monthlyIncome: candidate.monthly_income?.toString() || candidate.salaire_base_net?.toString() || '',
          familySize: (candidate.nombre_enfants || 0) + (candidate.autres_personnes_charge || 0),
          housingType: candidate.type_logement_souhaite?.join(', ') || ''
        });

        // TODO: Charger le nombre de candidatures depuis le backend
        // setApplicationsCount(candidate.applications?.length || 0);
      }
    } catch (err: any) {
      console.error('Error loading candidate data:', err);
      // Si pas de dossier, utiliser les données de l'utilisateur uniquement
      setProfileData({
        firstName: user?.first_name || '',
        lastName: user?.last_name || '',
        email: user?.email || '',
        phone: '',
        dateOfBirth: '',
        address: '',
        city: '',
        postalCode: '',
        poste_occupe: '',
        monthlyIncome: '',
        familySize: 0,
        housingType: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!candidateData?.id) {
      notification.toast.error('Impossible de mettre à jour le profil: dossier candidat non trouvé');
      return;
    }

    try {
      // Préparer les données à envoyer
      const updateData: Partial<Candidate> = {
        prenoms: profileData.firstName,
        nom: profileData.lastName,
        email: profileData.email,
        telephone_1: profileData.phone,
        date_naissance: profileData.dateOfBirth,
        quartier_actuel: profileData.address,
        commune: profileData.city,
        monthly_income: parseFloat(profileData.monthlyIncome) || 0,
      };

      const formData = new FormData();
      formData.append('candidate_data', JSON.stringify(updateData));

      const response = await CandidateServices.updateWithFiles(candidateData.id, formData);
      const result = response as { success: boolean; message?: string };

      if (result.success) {
        notification.toast.success('Profil mis à jour avec succès');
        setIsEditing(false);
        // Recharger les données
        loadCandidateData();
      } else {
        notification.toast.error(result.message || 'Erreur lors de la mise à jour');
      }
    } catch (err: any) {
      console.error('Error updating profile:', err);
      notification.toast.error('Erreur lors de la mise à jour du profil');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Recharger les données pour annuler les modifications
    loadCandidateData();
  };

  const getInitials = () => {
    return `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(0)}`;
  };

  const getStatusLabel = (status: string | undefined) => {
    if (!status) return 'N/A';
    const statusMap: Record<string, string> = {
      'draft': 'Brouillon',
      'submitted': 'Soumis',
      'verified': 'Vérifié',
      'complement_requis': 'Complément requis',
      'rejected': 'Rejeté'
    };
    return statusMap[status] || status;
  };

  const getProfileTypeLabel = (type: string | undefined) => {
    if (!type) return 'N/A';
    const typeMap: Record<string, string> = {
      'employee': 'Salarié',
      'entrepreneur': 'Entrepreneur',
      'informal': 'Informel'
    };
    return typeMap[type] || type;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          fontSize: theme.typography.sizes.lg,
          color: theme.colors.text + '60'
        }}>
          Chargement du profil...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Mon Profil</PageTitle>
        <PageSubtitle>
          Gérez vos informations personnelles et gardez votre profil à jour
        </PageSubtitle>
      </PageHeader>

      <ProfileContainer>
        <ProfileSummary>
          <Avatar>{getInitials()}</Avatar>
          <UserName>{profileData.firstName} {profileData.lastName}</UserName>
          <UserEmail>{profileData.email}</UserEmail>

          <ProfileStats>
            <StatItem>
              <StatValue>{applicationsCount}</StatValue>
              <StatLabel>Candidatures</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue style={{ fontSize: theme.typography.sizes.sm }}>{getStatusLabel(candidateData?.status)}</StatValue>
              <StatLabel>Statut</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{documentsCount}</StatValue>
              <StatLabel>Documents</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue style={{ fontSize: theme.typography.sizes.sm }}>{getProfileTypeLabel(candidateData?.profile_type??'')}</StatValue>
              <StatLabel>Type de profil</StatLabel>
            </StatItem>
          </ProfileStats>
        </ProfileSummary>

        <ProfileDetails>
          <SectionHeader>
            <SectionTitle>Informations personnelles</SectionTitle>
            {!isEditing ? (
              <EditButton onClick={() => setIsEditing(true)}>
                <FiEdit2 /> Modifier
              </EditButton>
            ) : (
              <CancelButton onClick={handleCancel}>
                <FiX /> Annuler
              </CancelButton>
            )}
          </SectionHeader>

          {isEditing ? (
            <Form onSubmit={handleSubmit}>
              <FormGrid>
                <FormGroup>
                  <Label>Prénom</Label>
                  <Input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Nom</Label>
                  <Input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                  />
                </FormGroup>

                <FormGroup fullWidth>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Téléphone</Label>
                  <Input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Date de naissance</Label>
                  <Input
                    type="date"
                    value={profileData.dateOfBirth}
                    onChange={(e) => setProfileData({...profileData, dateOfBirth: e.target.value})}
                  />
                </FormGroup>

                <FormGroup fullWidth>
                  <Label>Adresse</Label>
                  <Input
                    type="text"
                    value={profileData.address}
                    onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Ville</Label>
                  <Input
                    type="text"
                    value={profileData.city}
                    onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Code postal</Label>
                  <Input
                    type="text"
                    value={profileData.postalCode}
                    onChange={(e) => setProfileData({...profileData, postalCode: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Profession</Label>
                  <Input
                    type="text"
                    value={profileData.poste_occupe}
                    onChange={(e) => setProfileData({...profileData, poste_occupe: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Revenus mensuels ( FCFA)</Label>
                  <Input
                    type="number"
                    value={profileData.monthlyIncome}
                    onChange={(e) => setProfileData({...profileData, monthlyIncome: e.target.value})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Nombre de personnes</Label>
                  <Input
                    type="number"
                    value={profileData.familySize}
                    onChange={(e) => setProfileData({...profileData, familySize: parseInt(e.target.value)})}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Type de logement recherché</Label>
                  <Input
                    type="text"
                    value={profileData.housingType}
                    onChange={(e) => setProfileData({...profileData, housingType: e.target.value})}
                  />
                </FormGroup>
              </FormGrid>

              <ButtonGroup>
                <EditButton type="submit">
                  <FiSave /> Enregistrer
                </EditButton>
                <CancelButton type="button" onClick={handleCancel}>
                  <FiX /> Annuler
                </CancelButton>
              </ButtonGroup>
            </Form>
          ) : (
            <>
              <InfoItem>
                <InfoIcon><FiUser /></InfoIcon>
                <InfoContent>
                  <InfoLabel>Nom complet</InfoLabel>
                  <InfoValue>{profileData.firstName} {profileData.lastName}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon><FiMail /></InfoIcon>
                <InfoContent>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>{profileData.email}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon><FiPhone /></InfoIcon>
                <InfoContent>
                  <InfoLabel>Téléphone</InfoLabel>
                  <InfoValue>{profileData.phone}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon><FiCalendar /></InfoIcon>
                <InfoContent>
                  <InfoLabel>Date de naissance</InfoLabel>
                  <InfoValue>{new Date(profileData.dateOfBirth).toLocaleDateString('fr-FR')}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon><FiMapPin /></InfoIcon>
                <InfoContent>
                  <InfoLabel>Adresse</InfoLabel>
                  <InfoValue>{profileData.address}, {profileData.postalCode} {profileData.city}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon><FiBriefcase /></InfoIcon>
                <InfoContent>
                  <InfoLabel>Profession</InfoLabel>
                  <InfoValue>{profileData.poste_occupe}</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon><FiBriefcase /></InfoIcon>
                <InfoContent>
                  <InfoLabel>Revenus mensuels</InfoLabel>
                  <InfoValue>{profileData.monthlyIncome}  FCFA</InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon><FiUsers /></InfoIcon>
                <InfoContent>
                  <InfoLabel>Composition du foyer</InfoLabel>
                  <InfoValue>{profileData.familySize} personne(s)</InfoValue>
                </InfoContent>
              </InfoItem>

              {profileData.housingType && (
                <InfoItem>
                  <InfoIcon><FiHome /></InfoIcon>
                  <InfoContent>
                    <InfoLabel>Type de logement recherché</InfoLabel>
                    <InfoValue>{profileData.housingType}</InfoValue>
                  </InfoContent>
                </InfoItem>
              )}
            </>
          )}
        </ProfileDetails>
      </ProfileContainer>
    </DashboardLayout>
  );
};
